/**
 * Session-timeout / inactivity tuning. Centralised here so a future
 * runtime-config flip (see apps/web/public/runtime-config.js) can
 * override these without touching every consumer.
 *
 * Why these specific defaults:
 *   - Banking apps lean on 5/1 min — too aggressive for an ERP / shop.
 *   - Salesforce / Zoho / Tally default to 15-minute idle logout with
 *     a 1-minute warning. We match that so admins coming from those
 *     tools see familiar behaviour.
 *   - The active session also gets refreshed every 30 s by
 *     `useSessionBootstrap.ts` for revocation-detection — that's a
 *     server-side liveness check, NOT a user-activity timer. The two
 *     timers are independent.
 *
 * Bug ref: AI Agent 2026-06-11 — "session time out and screen not
 * refresh or inactivety by user then showing popup, click OK to
 * continue otherwise logout".
 */

/**
 * How long the user can be idle (no mouse / keyboard / scroll / touch)
 * before the warning modal opens. 14 minutes.
 */
export const IDLE_WARN_AFTER_MS = 14 * 60 * 1000;

/**
 * How long the user has to click "Stay signed in" after the warning
 * modal opens. When this elapses we dispatch `logout('inactive')`. 60 s.
 *
 * Effective max session inactivity = `IDLE_WARN_AFTER_MS + IDLE_COUNTDOWN_MS`
 * ≈ 15 minutes total before forced logout.
 */
export const IDLE_COUNTDOWN_MS = 60 * 1000;

/**
 * Optional pre-warning when the access token itself is about to
 * expire — independent of idle. If the access token has ≤60 s left
 * AND the user hasn't been actively typing, we ALSO show the warning
 * modal so they can refresh proactively rather than hit a 401 mid-
 * action.
 *
 * Disabled (Infinity) for now — the access-token leeway in
 * `packages/api/src/core/client.ts` is 120 s and that path already
 * runs silent. Set to e.g. `60 * 1000` if we want a visible warning
 * on token expiry too.
 */
export const TOKEN_EXPIRY_WARN_MS = Number.POSITIVE_INFINITY;

/** Activity events to listen for. Passive listeners — zero perf impact. */
export const IDLE_ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
  'wheel',
] as const;

/**
 * How often the idle-watcher's `setInterval` ticks. 1 s gives us
 * sub-second countdown precision in the modal without thrashing
 * React's reconciler.
 */
export const IDLE_TICK_MS = 1000;
